import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, Box, Typography, TextField, Button, Stack, FormControlLabel, Checkbox, Checkbox as MuiCheckbox } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getUsuarios } from '../../api';
import { useUnidades } from '../../../hooks/useUnidades';
import { generateLeadsReport } from './LeadsPdf';
import { cores } from '../../../styles/styleUtils';


const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 900px;
  max-width: 90vw;
  background-color: ${cores.cinzaClaro};
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 10px;
`;

const FormColumn = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CustomTextField = styled(TextField)`
  background-color: white;
  border-radius: 20px;
  & .MuiOutlinedInput-root {
    border-radius: 20px;
  }
  & .MuiOutlinedInput-input {
    padding: 10px 14px;
  }
`;

const FieldLabel = styled(Typography)`
  font-weight: bold;
  min-width: 120px;
  text-align: right;
  padding-top: 5px;
  margin-right: 15px;
`;

const ScrollableListbox = styled(Box)`
  max-height: 120px;
  overflow-y: auto;
  background-color: ${cores.branco};
  width: 100%;
  border-radius: 10px;
  padding: 5px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.5);
    border-radius: 10px;
  }
`;

function ModalExportLeads({ open, onClose, dashboardData, currentFilters }) {
    // ATENÇÃO: currentFilters.unidades precisa vir como array de _id (esquema
    // novo) pra bater com o filtro abaixo — isso depende do PopoverFiltroLeads.js
    // (já migrado) estar alimentando essa prop. Ver também o alerta de risco
    // sobre /dadoshomeleads no comentário do PopoverFiltroLeads.js.
    const [filtros, setFiltros] = useState({
        dataInicio: currentFilters.dataInicio || '',
        dataFim: currentFilters.dataFim || '',
        usuarios: [...(currentFilters.usuarios || [])],
        unidades: [...(currentFilters.unidades || [])],
        graficos: ['funil-chart', 'status-chart', 'desqualificados-chart', 'perdidos-chart']
    });

    const currentLocation = useLocation();
    const usuarioLogado = JSON.parse(localStorage.getItem('userData'))

    const { unidades } = useUnidades();
    const [options, setOptions] = useState({ usuarios: [] });

    useEffect(() => {
        const fetchDados = async () => {
            try {
                const u = await getUsuarios();
                setOptions(prev => ({ ...prev, usuarios: u }));
            } catch (e) { console.error(e) }
        };
        fetchDados();
    }, []);

    const handleDateChange = (e) => setFiltros({ ...filtros, [e.target.name]: e.target.value });

    const toggleArrayItem = (key, val) => {
        const arr = filtros[key];
        if (arr.includes(val)) setFiltros({ ...filtros, [key]: arr.filter(v => v !== val) });
        else setFiltros({ ...filtros, [key]: [...arr, val] });
    };

    const handleGerar = () => {
        // Collect filter info for the PDF
        const dataAtual = new Date();
        const dataGeracao = `${dataAtual.getDate().toString().padStart(2, '0')}/${(dataAtual.getMonth() + 1).toString().padStart(2, '0')}/${dataAtual.getFullYear()}`;

        const filterInfo = {
            dataInicio: filtros.dataInicio.split('-').reverse().join('/'),
            dataFim: filtros.dataFim.split('-').reverse().join('/'),
            consultor: filtros.usuarios.length === 0 ? "Todos" : options.usuarios.filter(u => filtros.usuarios.includes(u._id)).map(u => u.nome).join(', '),
            unidade: filtros.unidades.length === 0 ? "Todas" : unidades.filter(u => filtros.unidades.includes(u._id)).map(u => u.nome).join(', '),
            dataGeracao,
            geradoPor: usuarioLogado.nome || 'Usuário',
        };

        generateLeadsReport(dashboardData, filterInfo, filtros.graficos);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalContainer>
                <Typography variant="h4" fontWeight="bold" mb={2}>Exportar Relatório</Typography>

                <FormRow>
                    <FormColumn >

                        <Box display="flex">
                            <FieldLabel>Início:</FieldLabel>
                            <CustomTextField type="date" name="dataInicio" value={filtros.dataInicio} onChange={handleDateChange} fullWidth />
                        </Box>

                        <Box display="flex">
                            <FieldLabel>Consultor:</FieldLabel>
                            <Box flex={1}>
                                {options.usuarios.length > 0 ? (
                                    <ScrollableListbox>
                                        {options.usuarios.map(u => (
                                            <FormControlLabel
                                                key={u._id}
                                                control={<Checkbox sx={{ color: cores.chumbo, '&.Mui-checked': { color: cores.laranja } }} checked={filtros.usuarios.includes(u._id)} onChange={() => toggleArrayItem('usuarios', u._id)} />}
                                                label={u.nome}
                                                sx={{ color: '#333' }}
                                                disabled={currentLocation.pathname === '/leads' && !usuarioLogado.supervisor}
                                            />
                                        ))}
                                    </ScrollableListbox>
                                ) : <Typography variant="caption">Carregando...</Typography>}
                            </Box>
                        </Box>

                        <Box display="flex">
                            <FieldLabel>Unidade:</FieldLabel>
                            <Box flex={1}>
                                <ScrollableListbox>
                                    {unidades.map(u => (
                                        <FormControlLabel
                                            key={u._id}
                                            control={<Checkbox sx={{ color: cores.chumbo, '&.Mui-checked': { color: cores.laranja } }} checked={filtros.unidades.includes(u._id)} onChange={() => toggleArrayItem('unidades', u._id)} />}
                                            label={u.nome}
                                            sx={{ color: '#333' }}
                                        />
                                    ))}
                                </ScrollableListbox>
                            </Box>
                        </Box>

                    </FormColumn>

                    <FormColumn>
                        <Box display="flex">
                            <FieldLabel>Término:</FieldLabel>
                            <CustomTextField type="date" name="dataFim" value={filtros.dataFim} onChange={handleDateChange} fullWidth />
                        </Box>

                    </FormColumn>
                </FormRow>

                <FormRow>
                    <Box display="flex">
                        <FieldLabel>Gráficos:</FieldLabel>
                        <ScrollableListbox>
                            <FormControlLabel
                                control={<Checkbox sx={{ color: cores.chumbo, '&.Mui-checked': { color: cores.laranja } }} checked={filtros.graficos.includes('funil-chart')} onChange={() => toggleArrayItem('graficos', 'funil-chart')} />}
                                label="Funil de Leads"
                                sx={{ display: 'block' }}
                            />
                            <FormControlLabel
                                control={<Checkbox sx={{ color: cores.chumbo, '&.Mui-checked': { color: cores.laranja } }} checked={filtros.graficos.includes('status-chart')} onChange={() => toggleArrayItem('graficos', 'status-chart')} />}
                                label="Status Leads"
                                sx={{ display: 'block' }}
                            />
                            <FormControlLabel
                                control={<Checkbox sx={{ color: cores.chumbo, '&.Mui-checked': { color: cores.laranja } }} checked={filtros.graficos.includes('desqualificados-chart')} onChange={() => toggleArrayItem('graficos', 'desqualificados-chart')} />}
                                label="Leads Desqualificados"
                                sx={{ display: 'block' }}
                            />
                            <FormControlLabel
                                control={<Checkbox sx={{ color: cores.chumbo, '&.Mui-checked': { color: cores.laranja } }} checked={filtros.graficos.includes('perdidos-chart')} onChange={() => toggleArrayItem('graficos', 'perdidos-chart')} />}
                                label="Projetos Perdidos"
                                sx={{ display: 'block' }}
                            />
                        </ScrollableListbox>
                    </Box>


                </FormRow>

                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button variant="contained" sx={{ bgcolor: cores.laranja, '&:hover': { bgcolor: cores.laranjaEscuro }, textTransform: 'none', borderRadius: 5, px: 4, fontWeight: 'bold' }} onClick={handleGerar}>
                        Gerar Relatório
                    </Button>
                    <Button variant="contained" sx={{ bgcolor: '#dc3545', '&:hover': { bgcolor: '#c82333' }, textTransform: 'none', borderRadius: 5, px: 4, fontWeight: 'bold' }} onClick={onClose}>
                        Cancelar
                    </Button>
                </Box>

            </ModalContainer>
        </Modal>
    );
}

export default ModalExportLeads;